import { z } from 'zod';
import { Appointment } from './appointments.js';
import { IsoDate, IsoDateTime, TimeOfDay, Uuid } from '../primitives.js';

/**
 * WS7 phase 1 — scheduling depth contract: time off, salon closed dates, and date-specific
 * working-hour exceptions. These are SALON-wide calendar facts (not renter-sealed) the availability
 * engine consumes so the diary reflects who is actually in and when the salon is actually open.
 *
 * TODO(WS7 phase 2): booking-policy engine (deposit/cancellation/no-show rules) hooks in alongside
 * these — it reads the same per-salon scope but enforces money rules around payments.
 */

/** Why a staff member is away. */
export const TimeOffReason = z.enum(['holiday', 'sick', 'training', 'other']);
export type TimeOffReason = z.infer<typeof TimeOffReason>;

// ── Time off ──────────────────────────────────────────────────────────────────

export const TimeOff = z
  .object({
    id: Uuid,
    salonId: Uuid,
    staffId: Uuid,
    startDate: IsoDate,
    endDate: IsoDate.describe('inclusive — the staff member is off through this date'),
    reason: TimeOffReason,
    note: z.string().max(500).nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'TimeOff' });
export type TimeOff = z.infer<typeof TimeOff>;

/** Create a time-off entry for the staff member named in the path. */
export const CreateTimeOff = z
  .object({
    startDate: IsoDate,
    endDate: IsoDate,
    reason: TimeOffReason.default('holiday'),
    note: z.string().max(500).nullish(),
  })
  .meta({ id: 'CreateTimeOff' });
export type CreateTimeOff = z.infer<typeof CreateTimeOff>;

/**
 * Result of creating time off: the new entry PLUS any of that staff member's existing non-cancelled
 * appointments that fall inside the off period. We warn, never auto-cancel — the caller surfaces a
 * rebook/notify flow so an appointment is never silently stranded.
 */
export const TimeOffConflictResult = z
  .object({
    timeOff: TimeOff,
    conflicts: z
      .array(Appointment)
      .describe('appointments overlapping the time-off range; a WARNING'),
  })
  .meta({ id: 'TimeOffConflictResult' });
export type TimeOffConflictResult = z.infer<typeof TimeOffConflictResult>;

// ── Closed dates ─────────────────────────────────────────────────────────────

export const ClosedDate = z
  .object({
    id: Uuid,
    salonId: Uuid,
    date: IsoDate,
    reason: z.string().max(200).nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ClosedDate' });
export type ClosedDate = z.infer<typeof ClosedDate>;

export const CreateClosedDate = z
  .object({
    date: IsoDate,
    reason: z.string().max(200).nullish(),
  })
  .meta({ id: 'CreateClosedDate' });
export type CreateClosedDate = z.infer<typeof CreateClosedDate>;

// ── Working-hour exceptions ──────────────────────────────────────────────────

export const HourException = z
  .object({
    id: Uuid,
    salonId: Uuid,
    staffId: Uuid,
    date: IsoDate,
    isClosed: z.boolean().describe('true → the staff member is off this date entirely'),
    startTime: TimeOfDay.nullable().describe('custom hours that REPLACE the weekday band'),
    endTime: TimeOfDay.nullable(),
    breakStart: TimeOfDay.nullable().describe('optional mid-day break that splits the band'),
    breakEnd: TimeOfDay.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'HourException' });
export type HourException = z.infer<typeof HourException>;

/**
 * Upsert a working-hour exception keyed on (staff, date). When `isClosed` is false a start/end band
 * is required (validated server-side too, against the DB CHECK); a break, if present, needs both
 * ends. Times are wall-clock "HH:MM" in the salon timezone, like working hours.
 */
export const SetHourException = z
  .object({
    date: IsoDate,
    isClosed: z.boolean().default(false),
    startTime: TimeOfDay.nullish(),
    endTime: TimeOfDay.nullish(),
    breakStart: TimeOfDay.nullish(),
    breakEnd: TimeOfDay.nullish(),
  })
  .meta({ id: 'SetHourException' });
export type SetHourException = z.infer<typeof SetHourException>;

/** Optional date-range filter for listing a staff member's hour exceptions. */
export const HourExceptionQuery = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type HourExceptionQuery = z.infer<typeof HourExceptionQuery>;

/** Optional date-range filter for listing a staff member's time off. */
export const TimeOffQuery = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type TimeOffQuery = z.infer<typeof TimeOffQuery>;
